import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { ActionMenu, Button, DataTable, Dialog, EmptyState, Field, FormError, formatDate, Input, Panel, Select, StatusBadge, toast } from "@hostdock/ui";
import { api } from "../api";
import { useSession } from "../session";

const REASON_LABEL: Record<string, string> = { memory: "RAM", cpu: "CPU", processes: "processes" };

/** Shows whether a package pool is at its hard resource limit or over its monthly bandwidth — what the customer sees. */
export function ResourceState({ resourceLimit, bandwidthExceeded }: { resourceLimit: { reason: string | null; since: string | null } | null; bandwidthExceeded: boolean }) {
  if (!resourceLimit && !bandwidthExceeded) return <span className="text-[11px] text-muted">Within limits</span>;
  const pill = "inline-flex items-center gap-1 rounded-full bg-danger-soft px-2 py-0.5 text-[10.5px] font-medium text-danger";
  return (
    <span className="flex flex-wrap gap-1">
      {resourceLimit && <span className={pill} title={resourceLimit.since ? `Since ${formatDate(resourceLimit.since)}` : undefined}>Resource limit{resourceLimit.reason ? ` · ${REASON_LABEL[resourceLimit.reason] ?? resourceLimit.reason}` : ""}</span>}
      {bandwidthExceeded && <span className={pill}>Bandwidth exceeded</span>}
    </span>
  );
}

export function CustomerSubscriptions({ customerId, customerActive }: { customerId: string; customerActive: boolean }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["subscriptions", customerId], queryFn: () => api.subscriptions.list(customerId) });
  const plans = useQuery({ queryKey: ["plans"], queryFn: api.plans.list, enabled: can("BILLING_MANAGE") });
  const [adding, setAdding] = useState(false);
  const [planId, setPlanId] = useState("");
  const [ref, setRef] = useState("");
  const [regionId, setRegionId] = useState("");
  const [serverId, setServerId] = useState("");
  const regions = useQuery({ queryKey: ["regions"], queryFn: api.regions.list, enabled: can("BILLING_MANAGE") });
  const servers = useQuery({
    queryKey: ["servers-region", regionId],
    queryFn: () => api.servers.list({ regionId, status: "ACTIVE", pageSize: 100 }),
    enabled: Boolean(regionId) && can("SERVER_MANAGE"),
  });
  const plan = plans.data?.items.find((x) => x.id === planId);
  const planRegions = regions.data?.items.filter((r) => r.status === "ACTIVE" && (!plan?.regionIds.length || plan.regionIds.includes(r.id)));
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["subscriptions", customerId] }); void qc.invalidateQueries({ queryKey: ["subscriptions-all"] }); };
  const create = useMutation({ meta: { inlineError: true }, mutationFn: () => api.subscriptions.create(customerId, planId, ref, { regionId, serverId }), onSuccess: () => { toast.success("Plan assigned"); setAdding(false); setPlanId(""); setRef(""); setRegionId(""); setServerId(""); refresh(); } });
  const update = useMutation({ mutationFn: (v: { id: string; status: string }) => api.subscriptions.update(customerId, v.id, { status: v.status }), onSuccess: () => { toast.success("Subscription updated"); refresh(); }, onError: (e) => toast.error(e.message) });
  return (
    <Panel title="Subscriptions" className="mt-5" flush actions={can("BILLING_MANAGE") && customerActive && <Button size="sm" icon={<Plus size={13} />} onClick={() => setAdding(true)}>Assign plan</Button>}>
      <DataTable
        rows={q.data?.items}
        loading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        rowKey={(s) => s.id}
        skeletonRows={2}
        empty={<EmptyState title="No hosting plan" description="Assign a plan so this customer can create websites." />}
        columns={[
          { key: "p", header: "Plan", cell: (s) => <span className="font-medium">{s.plan.name}</span> },
          { key: "s", header: "Status", cell: (s) => <StatusBadge status={s.status === "CANCELLED" ? "CLOSED" : s.status} /> },
          { key: "l", header: "Location", cell: (s) => <span className="text-muted">{s.region ? `${s.region.name}${s.server ? ` · ${s.server.name}` : ""}` : "Automatic"}</span>, hideOnMobile: true },
          { key: "u", header: "Usage", cell: (s) => <span className="tabular-nums text-muted">{s.usage.websites} sites · {s.usage.databases} DBs</span> },
          { key: "r", header: "Resources", cell: (s) => <ResourceState resourceLimit={s.resourceLimit} bandwidthExceeded={s.bandwidthExceeded} /> },
          { key: "d", header: "Since", cell: (s) => <span className="text-muted">{formatDate(s.createdAt)}</span>, hideOnMobile: true },
          {
            key: "a", header: "", className: "w-10 text-right", cell: (s) => can("BILLING_MANAGE") && (
              <ActionMenu actions={[
                { label: "Suspend", onSelect: () => update.mutate({ id: s.id, status: "SUSPENDED" }), hidden: s.status !== "ACTIVE" },
                { label: "Reactivate", onSelect: () => update.mutate({ id: s.id, status: "ACTIVE" }), hidden: s.status !== "SUSPENDED" },
                { label: "Cancel", onSelect: () => update.mutate({ id: s.id, status: "CANCELLED" }), hidden: s.status === "CANCELLED", danger: true },
              ]} />
            ),
          },
        ]}
      />
      <Dialog open={adding} onOpenChange={setAdding} title="Assign plan" size="sm" footer={<><Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button><Button variant="primary" loading={create.isPending} disabled={!planId} onClick={() => create.mutate()}>Assign</Button></>}>
        <div className="flex flex-col gap-4">
          <Field label="Plan">{(p) => <Select {...p} value={planId} onChange={(e) => { setPlanId(e.target.value); setRegionId(""); setServerId(""); }}><option value="">Select a plan…</option>{plans.data?.items.filter((x) => x.status === "ACTIVE").map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</Select>}</Field>
          <Field label="Region" hint="Customers cannot choose where their websites run. Leave on Automatic to place the package on the first region with capacity.">
            {(p) => <Select {...p} value={regionId} onChange={(e) => { setRegionId(e.target.value); setServerId(""); }}><option value="">Automatic</option>{planRegions?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>}
          </Field>
          {regionId && can("SERVER_MANAGE") && (
            <Field label="Server" hint="Optional. Pin this package to one server instead of letting the scheduler pick.">
              {(p) => <Select {...p} value={serverId} onChange={(e) => setServerId(e.target.value)}><option value="">Automatic</option>{servers.data?.items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>}
            </Field>
          )}
          <Field label="Billing reference (optional)" hint="e.g. the WHMCS service ID">{(p) => <Input {...p} value={ref} onChange={(e) => setRef(e.target.value)} />}</Field>
          <FormError error={create.error} />
        </div>
      </Dialog>
    </Panel>
  );
}
